/* ============================================================
   Habit cadence vocabulary — the shapes the app and backend both speak.

   Deliberately narrower than the task recurrence vocabulary (see
   docs/habit-vs-recurring-task.md): a habit's cadence is a *yardstick* for a
   consistency trend, not calendar precision, so it gets three shapes and stops.
   Widening the union costs no migration — the column is jsonb.

   Days here are floating-local "YYYY-MM-DD" calendar days, matching the
   recurrence module. Weekday codes are the RFC 5545 ones already used by BYDAY,
   so the whole repo has exactly one weekday encoding.
   ============================================================ */
import type { Weekday } from '../recurrence/types';

/**
 * Which days a habit intends to be done — the denominator of its consistency.
 *
 * - `daily` — every day is a scheduled day.
 * - `weekdays` — only the listed weekdays count; the rest are NEUTRAL (an
 *   unscheduled day is never a miss, and the habit doesn't surface that day).
 * - `timesPerWeek` — a weekly *quota*: the habit is offered every day and any
 *   `times` distinct days satisfy the week. Deliberately a day-count, not a rep
 *   accumulator — three reps crammed into one day is one active day, and the
 *   behaviour only wires through returning to it (see docs/habit-schedules.md).
 */
export type HabitSchedule =
  | { kind: 'daily' }
  | { kind: 'weekdays'; days: Weekday[] }
  | { kind: 'timesPerWeek'; times: number };

/** The default cadence — what every habit had before schedules existed. */
export const DAILY_SCHEDULE: HabitSchedule = { kind: 'daily' };

/** Bounds on `timesPerWeek`. 7 is deliberately excluded: it *is* `daily`, and
    allowing both would give the same cadence two representations (and two
    different consistency denominators). `normalizeSchedule` collapses it. */
export const MIN_TIMES_PER_WEEK = 1;
export const MAX_TIMES_PER_WEEK = 6;

/** How long a consistency trend looks back, in days. Four whole weeks: recent
    enough to read as "how am I doing lately", and divides evenly into weekly
    periods so `timesPerWeek` windows aren't clipped. */
export const CONSISTENCY_WINDOW_DAYS = 28;

/** RFC 5545 weekday code → `Date#getUTCDay()` index (0 = Sunday). */
export const WEEKDAY_INDEX: Record<Weekday, number> = {
  SU: 0,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
};

/** `getUTCDay()` index → RFC 5545 weekday code. A `const` tuple rather than a
    plain `readonly Weekday[]` so a Zod `z.enum(WEEKDAY_CODES)` at the API edge can
    consume it directly — one list of weekday codes for the whole repo. The
    `satisfies` keeps it honest against the `Weekday` union. */
export const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const satisfies
  readonly Weekday[];

/** Short display labels, indexed like `WEEKDAY_CODES`. */
export const WEEKDAY_LABELS: readonly string[] = [
  'Sun',
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
];
